<script setup lang="ts">
import { computed } from "vue";
import Button from "@/shared/ui/Button.vue";
import type { PlanJson } from "@/shared/api/assistant";

const props = defineProps<{
  plan: PlanJson;
  loading?: boolean;
}>();

const emit = defineEmits<{
  approve: [];
  reject: [];
}>();

const N1_TOOLS = new Set([
  "send_email",
  "delete_emails",
  "delete_contact",
  "upsert_event",
  "delete_event",
  "respond_invitation",
]);
const N2_TOOLS = new Set(["upsert_contact"]);

const TOOL_ICONS: Record<string, string> = {
  search_emails: "🔍",
  get_email: "📧",
  get_summary: "📝",
  draft_reply: "✏️",
  send_email: "📨",
  delete_emails: "🗑️",
  label_emails: "🏷️",
  move_emails: "📂",
  upsert_contact: "👤",
  delete_contact: "🚫",
  upsert_event: "📅",
  delete_event: "❌",
  respond_invitation: "✉️",
  find_slot: "🕐",
};

function levelOf(tool: string): "N1" | "N2" | "N3" {
  if (N1_TOOLS.has(tool)) return "N1";
  if (N2_TOOLS.has(tool)) return "N2";
  return "N3";
}

const totals = computed(() => ({
  outbound: props.plan.estimated_outbound_actions,
  destructive: props.plan.estimated_destructive_actions,
}));
</script>

<template>
  <article class="plan-card" role="region" aria-label="Plan d'action propose">
    <header class="plan-head">
      <h3>Plan propose</h3>
      <p class="summary">{{ plan.summary_for_user }}</p>
      <div class="counts">
        <span v-if="totals.outbound > 0" class="count outbound"
          >{{ totals.outbound }} envoi{{ totals.outbound > 1 ? "s" : "" }}</span
        >
        <span v-if="totals.destructive > 0" class="count destructive"
          >{{ totals.destructive }} suppression{{ totals.destructive > 1 ? "s" : "" }}</span
        >
      </div>
    </header>

    <ol class="steps">
      <li v-for="step in plan.steps" :key="step.step_id" class="step">
        <span class="icon">{{ TOOL_ICONS[step.tool] ?? "•" }}</span>
        <span class="label">{{ step.human_label }}</span>
        <span :class="['badge', `level-${levelOf(step.tool).toLowerCase()}`]">{{
          levelOf(step.tool)
        }}</span>
        <span v-if="step.dry_run_preview" class="badge dry">Apercu</span>
      </li>
    </ol>

    <footer class="actions">
      <Button variant="primary" :loading="loading ?? false" @click="emit('approve')"
        >Tout approuver</Button
      >
      <Button variant="ghost" :disabled="loading ?? false" @click="emit('reject')">Rejeter</Button>
    </footer>
  </article>
</template>

<style scoped>
.plan-card {
  background: var(--color-navy-light);
  border: 1px solid rgba(232, 160, 32, 0.2);
  border-radius: 8px;
  padding: 1rem;
  margin-top: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}
.plan-head h3 {
  font-family: var(--font-serif);
  font-weight: 400;
  font-size: 1.05rem;
  color: var(--color-or);
  margin: 0;
}
.summary {
  color: var(--color-cream);
  font-size: 0.9rem;
  margin: 0.3rem 0;
}
.counts {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
}
.count {
  font-size: 0.75rem;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-family: var(--font-mono);
}
.count.outbound {
  background: rgba(232, 160, 32, 0.15);
  color: var(--color-or);
}
.count.destructive {
  background: rgba(184, 67, 74, 0.15);
  color: var(--color-danger);
}
.steps {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.step {
  display: grid;
  grid-template-columns: 1.4rem 1fr auto auto;
  gap: 0.5rem;
  align-items: center;
  padding: 0.5rem;
  background: var(--color-navy);
  border-radius: 6px;
  font-size: 0.85rem;
  color: var(--color-cream);
}
.step .label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.badge {
  font-size: 0.7rem;
  padding: 0.1rem 0.45rem;
  border-radius: 4px;
  font-family: var(--font-mono);
  letter-spacing: 0.05em;
}
.badge.level-n1 {
  background: rgba(184, 67, 74, 0.18);
  color: var(--color-danger);
}
.badge.level-n2 {
  background: rgba(232, 160, 32, 0.18);
  color: var(--color-or);
}
.badge.level-n3 {
  background: rgba(110, 178, 122, 0.18);
  color: var(--color-success);
}
.badge.dry {
  background: rgba(160, 160, 160, 0.2);
  color: var(--color-gray);
}
.actions {
  display: flex;
  gap: 0.6rem;
}
</style>
